"use client";

import { Button } from "@/components/atoms/Button";
import StarRating from "@/components/atoms/StarRating";
import YouTubeVideoOverlay from "@/components/atoms/YouTubeVideoOverlay";
import Carousel1 from "@/components/organisms/carousels/Carousel1";
import {
  carouselClick,
  ctaClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  videoAction,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { hexToRgba } from "@/utils/utils";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LEAD_DROP_FORM_SECTION_VARIANTS,
  LEAD_DROP_VARIANTS,
} from "../../../constants";
import { RootState } from "@/store/store";
import { BANNER1_FONT_FAMILY_MAP } from "./Banner1";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import { useIsMobile } from "@/hooks/useIsMobile";
import { LEADDROP_FORM_2_DESIGN_TIMER } from "@/widgets/leaddropform/LeaddropForm2";

interface BannerImage {
  id: number;
  image: string;
  priority: number;
  status: boolean;
  border_color?: string;
  video_link?: string; // Option to enable video on any image
  enable_video?: boolean;
}

interface BannerItem {
  id: number;
  icon: string;
  alt_icon: string;
  description: string;
  description_color?: string;
  priority: number;
  status: boolean;
}

interface AccreditationItem {
  id: number;
  image: string;
  alt_image: string;
  priority: number;
  status: boolean;
}

interface LearnerImage {
  id: number;
  image: string;
  description?: string;
  description_color?: string;
  priority: number;
  status: boolean;
}

// ── Constants ───────────────────────────────────────────────────────────────

const MAX_BANNER_IMAGES = 3;
const MAX_BULLET_ITEMS = 4;
const MAX_ACCREDITATION_LOGOS = 4;
const MAX_LEARNER_IMAGES = 3;
const MAX_LEARNER_TEXT_CHARS = 16;
const MAX_RATING_TEXT_CHARS = 17;

/**
 * Width of the lead form when it takes the hero's right-hand slot, per the
 * Figma hero frames: 440 @1080, 450 @1230, 475 @1430, 560 @1750.
 *
 * The form's own default is a fixed 375px card, which under-fills this slot at
 * every desktop size — hence the explicit ladder. Passed both to the form and to
 * the pre-mount placeholder so the two occupy identical space and the form's
 * arrival cannot shift the layout. Only ≥sm matters: the form never renders
 * below 1080.
 */
const LEAD_FORM_SLOT_WIDTH =
  "w-full sm:max-w-[440px] md:max-w-[450px] lg:max-w-[475px] xl:max-w-[560px]";

/** Best-effort MIME guess from a video URL so the browser can pick the right decoder. */
const getVideoMime = (src: string): string | undefined => {
  if (/\.webm(\?|$)/i.test(src)) return "video/webm";
  if (/\.mov(\?|$)/i.test(src)) return "video/quicktime";
  if (/\.ogg|\.ogv(\?|$)/i.test(src)) return "video/ogg";
  if (/\.mp4|\.m4v(\?|$)/i.test(src)) return "video/mp4";
  return undefined;
};

/**
 * Banner 2 default fonts aligned with Atlas hero (Figma node 587:3613 — HERO SECTION).
 * Tokens: [WF] H1 Semibold 40 (title_1), [DT] H1 Bold 48 (program title / title_2),
 * [WF] H2 Semibold 20 (subtitle), [WF] B1 Semibold 16 (bullets + CTAs), WF 20 (learners + rating).
 */
export const BANNER2_FONT_FAMILY_MAP = {
  section: "var(--font-google-sans)",
  preTitle: "var(--font-google-sans)",
  primaryHeading: "var(--font-merriweather)",
  secondaryHeading: "var(--font-google-sans)",
  body: "var(--font-google-sans)",
  cta: "var(--font-google-sans)",
  learners: "var(--font-google-sans)",
  rating: "var(--font-google-sans)",
} as const;

export type Banner2FontFamilyMap = typeof BANNER2_FONT_FAMILY_MAP;

// ── Component ───────────────────────────────────────────────────────────────

const Banner2 = (props: any) => {
  const { data: sectionData } = props;

  const dispatch = useDispatch();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState("");
  const [alumniTooltip, setAlumniTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
  }>({ visible: false, x: 0, y: 0 });
  const bannerRef = useRef<HTMLElement>(null);
  const alumniTextRef = useRef<HTMLDivElement>(null);

  const handleAlumniMouseEnter = useCallback(() => {
    if (alumniTextRef.current) {
      const el = alumniTextRef.current;
      const isTruncated = el.scrollWidth > el.clientWidth;
      if (!isTruncated) return;
      const rect = el.getBoundingClientRect();
      setAlumniTooltip({
        visible: true,
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
    }
  }, []);

  const handleAlumniMouseLeave = useCallback(() => {
    setAlumniTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  const {
    metadata = [], // Banner images (max 3)
    metadata_1 = [], // Items/features (max 4 bullets)
    metadata_2 = [], // Accreditation (max 4 logos)
    metadata_3 = [], // Learners images (max 3)
    title_1 = "Welcome to",
    title_1_color = "#FFFFFF",
    title_2 = "Master of Business Administration",
    title_2_gradient_start_color = "rgba(255, 215, 0, 1)",
    title_2_gradient_end_color = "rgba(255, 165, 0, 1)",
    title_2_gradient_angle = "90deg",
    subtitle = "Transform Your Career with Industry-Leading Education",
    subtitle_color = "#E0E0E0",
    horizontal_stripe_color = "#FFFFFF",
    backvariant = "variant_1",
    font_family_variant = "variant_2",
    gradient_color_1 = "#00D4EB",
    gradient_color_2 = "#000000",
    gradient_color_3 = "#000000",
    gradient_color_4 = "#5345C3",
    background_color = "#000000",
    learners_description = "<p><strong>10,000+</strong> Learners already enrolled</p>",
    carousel_color = "#00d4eb",
    rating_number = "4.8",
    rating_text = "Google Rating",
    rating_color = "#FFFFFF",
    rating_number_color = "#FFFFFF",
    download_brochure_text = "Download Brochure",
    button_color = "#FFFFFF",
    icon = "",
    alt_icon = "Download icon",
    icon_color = "#222222",
    apply_now_text = "Apply Now",
    apply_now_color = "#FFFFFF",
    apply_now_border_color = "#FFFFFF",
    slide_frequency = 5000,
    hero_video_url: background_video = "",
    status = true,
    brochure_link = "",
    accreditation_scroll_duration,
    show_rating = 0,
    show_learners = 0,
    enable_animation,
    enable_lead_form = false,
  } = sectionData || {};

  /**
   * Right-hand slot: media (image / video / carousel) OR the university's
   * configured inline lead form — never both.
   *
   * Desktop-only by design. Below `sm` (1080) the media always wins and this
   * flag is ignored, so mobile output is byte-identical to before. The learners
   * /rating strip belongs to the media option, so it hides on desktop when the
   * form is showing but keeps rendering on mobile.
   *
   * MySQL TINYINT(1) arrives as 0/1, "0"/"1" or a boolean depending on driver,
   * so coerce rather than trusting truthiness — "0" is a truthy string.
   */
  const showLeadForm =
    enable_lead_form === true ||
    enable_lead_form === 1 ||
    enable_lead_form === "1";

  /**
   * Gate the form's MOUNT (not just its visibility) on the desktop breakpoint.
   *
   * CSS-hiding it would still mount the component on mobile — running its
   * capture hook and firing a widget impression for a form the user can never
   * see. `useIsMobile` returns null until after mount, so `=== false` is only
   * true on a real ≥1080 viewport; until then the slot renders a reserved-height
   * placeholder so the form's arrival cannot shift the layout.
   */
  const isMobileViewport = useIsMobile(1080);
  const isDesktopViewport = isMobileViewport === false;

  const leaddropFormVariant = useSelector(
    (state: RootState) => state.leaddropform.variant,
  );
  const LeaddropFormComponent = useMemo(
    () =>
      leaddropFormVariant
        ? (LEAD_DROP_FORM_SECTION_VARIANTS[
            leaddropFormVariant as keyof typeof LEAD_DROP_FORM_SECTION_VARIANTS
          ] ?? null)
        : null,
    [leaddropFormVariant],
  );

  /**
   * Fallback: an editor can switch the slot to the lead form on a university
   * that has no active lead-form setting (or whose stored variant has no
   * component). Without this the hero would hide its media and render an empty
   * right-hand column — a broken hero rather than a degraded one. In that case
   * we keep the media instead.
   *
   * The variant only reaches Redux on the client, so it is legitimately absent
   * during SSR. Anchoring this on `isDesktopViewport` — false until after mount
   * — means a correctly configured tenant never evaluates it as "unavailable",
   * so the media is never briefly revealed before the form arrives.
   */
  const leadFormUnavailable = isDesktopViewport && !LeaddropFormComponent;
  const leadFormTakesSlot = showLeadForm && !leadFormUnavailable;

  const isAnimationEnabled =
    enable_animation === true ||
    enable_animation === 1 ||
    enable_animation === "1";

  const brochure = useSelector(
    (state: RootState) => state.globalslice?.course?.brochure || "",
  );

  const fonts = useMemo(
    () =>
      font_family_variant === "variant_1"
        ? BANNER1_FONT_FAMILY_MAP
        : BANNER2_FONT_FAMILY_MAP,
    [font_family_variant],
  );

  // ── Filter & sort active metadata (with max limits) ─────────────────────

  // Banner images: max 3
  const activeBannerImages: BannerImage[] = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return metadata
      .filter((item: BannerImage) => item.status)
      .sort((a: BannerImage, b: BannerImage) => a.priority - b.priority)
      .slice(0, MAX_BANNER_IMAGES);
  }, [metadata]);

  // Bullet items: max 4
  const activeItems: BannerItem[] = useMemo(() => {
    if (!metadata_1 || metadata_1.length === 0) return [];
    return [...metadata_1]
      .filter((item: BannerItem) => item.status)
      .sort((a: BannerItem, b: BannerItem) => a.priority - b.priority)
      .slice(0, MAX_BULLET_ITEMS);
  }, [metadata_1]);

  // Accreditation logos: max 4
  const activeAccreditation: AccreditationItem[] = useMemo(() => {
    if (!metadata_2 || metadata_2.length === 0) return [];
    return [...metadata_2]
      .filter((item: AccreditationItem) => item.status)
      .sort(
        (a: AccreditationItem, b: AccreditationItem) => a.priority - b.priority,
      )
      .slice(0, MAX_ACCREDITATION_LOGOS);
  }, [metadata_2]);

  // Learner / alumni images: max 3
  const activeLearnerImages: LearnerImage[] = useMemo(() => {
    if (!metadata_3 || metadata_3.length === 0) return [];
    return [...metadata_3]
      .filter((item: LearnerImage) => item.status)
      .sort((a: LearnerImage, b: LearnerImage) => a.priority - b.priority)
      .slice(0, MAX_LEARNER_IMAGES);
  }, [metadata_3]);

  // ── Clamp rating values ─────────────────────────────────────────────────

  // Support "4/5" format — extract numerator for star rating calculation
  const ratingForStars = useMemo(() => {
    if (!rating_number) return 0;
    const parts = String(rating_number).split("/");
    return parseFloat(parts[0]) || 0;
  }, [rating_number]);

  const clampedRatingNumber = rating_number;

  const clampedRatingText = rating_text;

  // ── Analytics ───────────────────────────────────────────────────────────

  // Track banner slide viewed when slide changes
  useEffect(() => {
    if (
      activeBannerImages.length > 0 &&
      currentSlideIndex < activeBannerImages.length
    ) {
      carouselClick({
        section: "banner_2",
        label: `banner_slide_${currentSlideIndex + 1}`,
      });
    }
  }, [currentSlideIndex, activeBannerImages]);

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (bannerRef.current && status) {
  //     FireWidgetViewedEvent(bannerRef, "banner_2");
  //   }
  // }, [status]);

  // ── CTA Handlers ────────────────────────────────────────────────────────

  // Primary CTA: Download Brochure
  // Behaviour: Lead form popup opens. Brochure downloads post successful lead
  // drop. If unsuccessful, option to trigger download manually.
  const handleDownloadBrochure = () => {
    ctaClick({
      section: "banner_2",
      component: "download_brochure_button",
      label: download_brochure_text,
      step: "1",
      entity: "banner_2",
      parentEntity: "banner_2",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: brochure || "",
      }),
    );
  };

  // Secondary CTA: Apply Now
  // Behaviour: Lead form popup opens. Registered-successfully card shown post
  // successful lead drop. Text is customisable through CMS.
  const handleApplyNow = () => {
    ctaClick({
      section: "banner_2",
      component: "apply_now_button",
      label: apply_now_text,
      step: "1",
      entity: "banner_2",
      parentEntity: "banner_2",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  };

  // Video play handler
  const handleVideoPlay = (videoUrl: string) => {
    if (videoUrl) {
      videoAction({
        section: "banner_2",
        label: videoUrl,
      });
      setActiveVideoUrl(videoUrl);
      setVideoOpen(true);
    }
  };

    // A single banner slide: image + optional video-play overlay.
  // Shared by both the carousel and the single-image path below so the two
  // markups can never drift (priority/sizes/video must stay identical).
  const renderBannerSlide = (item: BannerImage, index: number) => (
    <div
      key={item.id || index}
      className="relative w-full h-[308px] sm:h-[450px] md:h-[527px] lg:h-[560px]"
    >
      {item.image && (
        <Image
          src={item.image}
          alt={`Banner image ${index + 1}`}
          fill
          className="object-cover"
          priority={index === 0}
          fetchPriority={index === 0 ? "high" : "auto"}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 660px"
        />
      )}
      {/* Video play button overlay when video is enabled */}
      {item.enable_video && item.video_link && (
        <button
          onClick={() => handleVideoPlay(item.video_link!)}
          className="absolute inset-0 z-10 flex items-center justify-center bg-black/20 transition-opacity hover:bg-black/30 cursor-pointer"
          aria-label="Play video"
          data-cta-name="video_play"
        >
          <div className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
            <svg
              className="w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 ml-1"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </button>
      )}
    </div>
  );

  // ── Early return ────────────────────────────────────────────────────────

  // A background video can stand in for the image carousel, so the banner is
  // still valid when there are no images but a video has been uploaded.
  if (!status || (activeBannerImages.length === 0 && !background_video)) {
    return null;
  }

  // ── Gradient background helper ──────────────────────────────────────────

  const getGradientBackground = () => {
    switch (backvariant) {
      case "variant_1":
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
      case "variant_2":
        return `linear-gradient(292deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
      case "variant_3":
        return background_color;
      default:
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      {/* Marquee keyframes for accreditation scroll */}
      <style>{`
        @keyframes banner2-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <section
        ref={bannerRef}
        className="w-full relative overflow-hidden"
        style={{
          backgroundColor: background_color,
          fontFamily: fonts.section,
        }}
      >
        {/* Background Gradient Overlay */}
        <div
          className="absolute inset-0 z-0"
          style={{
            background: getGradientBackground(),
          }}
        />

        {/* Main Content - Two Column Layout */}
        <div className="relative z-10 w-full max-w-[1080px] md:max-w-[1230px] lg:max-w-[1430px] xl:max-w-[1750px] mx-auto px-0 sm:px-[60px] md:px-[80px] lg:px-[80px] xl:px-[140px] py-0 sm:py-10 md:py-[50px] lg:pt-[60px] lg:pb-[116px] xl:pt-[80px] xl:pb-[80px]">
          <div className="flex flex-col sm:flex-row items-center sm:items-stretch gap-1 sm:gap-10 md:gap-2">
            {/* ─── Left Column - Content ─────────────────────────────── */}
            {/* On mobile, 'contents' makes children direct flex children for reordering */}

            <div className="contents sm:flex sm:flex-1 sm:flex-col sm:items-start sm:text-left sm:max-w-[425px] md:max-w-[487px] lg:max-w-[492px]">
              {/* Titles — mobile: order-1 */}
              <div className="order-1 sm:order-0 w-full flex flex-col gap-[14px] sm:gap-0 items-center sm:items-start text-center sm:text-left px-6 sm:px-0 pt-6 sm:pt-0 pb-2 sm:pb-0">
                {/* Title 1 (small pre-title) */}
                {title_1 && (
                  <RevealOnScroll
                    y={0}
                    x={0}
                    duration={1000}
                    enableAnimation={isAnimationEnabled}
                  >
                    <h1
                      className="text-[24px] sm:text-[28px] md:text-[30px] lg:text-[40px] font-semibold sm:mb-1 truncate max-w-full"
                      style={{
                        color: title_1_color,
                        opacity: 0.8,
                        fontFamily: fonts.preTitle,
                      }}
                    >
                      {title_1}
                    </h1>
                  </RevealOnScroll>
                )}

                {/* Program Title (title_2)
                  Max lines: 3
                  Behaviour: Styling option through curly braces in CMS
                  Rendered as HTML to support rich-text / curly-brace styling */}
                {title_2 && (
                  <RevealOnScroll
                    threshold={0}
                    duration={500}
                    enableAnimation={isAnimationEnabled}
                  >
                    <h2
                      className="text-[32px] sm:text-[34px] md:text-[40px] lg:text-[48px] font-bold sm:mb-3 leading-120 tracking-[0.96px] sm:tracking-[1.02px] md:tracking-[1.2px] lg:-tracking-144 line-clamp-3"
                      style={{
                        fontFamily: fonts.primaryHeading,
                        backgroundImage: `linear-gradient(${title_2_gradient_angle}, ${title_2_gradient_start_color}, ${title_2_gradient_end_color})`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                      dangerouslySetInnerHTML={{ __html: title_2 }}
                    />
                  </RevealOnScroll>
                )}

                {/* Program Subtitle / Description
                  Max lines: 1 — truncate with ellipsis */}
                {subtitle && (
                  <RevealOnScroll y={20} enableAnimation={isAnimationEnabled}>
                    <p
                      className="text-[18px] line-clamp-2 sm:line-clamp-1 sm:text-[15px] md:text-[16px] lg:text-[20px] leading-[1.5] sm:mb-4 max-w-full"
                      style={{
                        color: subtitle_color,
                        fontFamily: fonts.secondaryHeading,
                      }}
                    >
                      {subtitle}
                    </p>
                  </RevealOnScroll>
                )}
              </div>

              {/* Horizontal Stripe — hidden on mobile */}
              <div
                className="hidden sm:block w-[100px] h-[3px] mb-4"
                style={{ backgroundColor: horizontal_stripe_color }}
              />

              {/* Key Details (Bullets) — mobile: order-6 */}
              {activeItems.length > 0 && (
                <div className="order-6 sm:order-0 flex flex-col gap-3 sm:gap-2.5 md:gap-4 sm:mb-6 w-full px-6 sm:px-0 p-6 sm:pb-0">
                  {activeItems.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      className="flex items-center gap-[11px] min-w-0"
                    >
                      {/* Bullet icon or fallback tick-circle */}
                      <RevealOnScroll
                        x={0}
                        y={0}
                        threshold={0}
                        scaleStart={0.5}
                        delay={500}
                        enableAnimation={isAnimationEnabled}
                      >
                        {item.icon ? (
                          <div className="relative w-5 h-5 xs:w-[18px] xs:h-[18px] sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 shrink-0">
                            <Image
                              src={item.icon}
                              alt={item.alt_icon || "Bullet icon"}
                              fill
                              className="object-contain"
                            />
                          </div>
                        ) : (
                          <svg
                            className="w-5 h-5 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 shrink-0"
                            viewBox="0 0 24 24"
                            fill={item.description_color || "#FFFFFF"}
                          >
                            <path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm4.78 7.7l-5.67 5.67a.75.75 0 01-1.06 0l-2.83-2.83a.75.75 0 111.06-1.06l2.3 2.3 5.14-5.14a.75.75 0 011.06 1.06z" />
                          </svg>
                        )}
                      </RevealOnScroll>

                      <RevealOnScroll
                        y={10}
                        delay={500}
                        enableAnimation={isAnimationEnabled}
                      >
                        <span
                          className="text-sm sm:text-xs md:text-sm lg:text-[16px] font-semibold -tracking-012 md:-tracking-014 lg:-tracking-016 line-clamp-2 sm:line-clamp-none sm:truncate min-w-0"
                          style={{
                            color: item.description_color || "#FFFFFF",
                            fontFamily: fonts.body,
                          }}
                        >
                          {item.description}
                        </span>
                      </RevealOnScroll>
                    </div>
                  ))}
                </div>
              )}

              {/* Accreditations — mobile: order-5, scrolling marquee on all sizes */}
              
                {activeAccreditation.length > 0 && (
                  <div className="order-5 sm:order-0 w-full px-6 sm:px-0 py-2 sm:py-0 sm:mb-8">
                    <div className="overflow-hidden">
                      <RevealOnScroll
                x={10}
                y={10}
                threshold={0}
                scaleStart={0}
                delay={500}
                enableAnimation={isAnimationEnabled}
              >
                      <div
                        className="flex items-center gap-4 sm:gap-6 w-max hover:[animation-play-state:paused]"
                        style={{
                          animation: `banner2-marquee ${(accreditation_scroll_duration ?? 20000) / 1000}s linear infinite`,
                        }}
                      >
                        {[...activeAccreditation, ...activeAccreditation].map(
                          (item, idx) =>
                            item.image && (
                              <div
                                key={`accreditation-${idx}`}
                                className={cn(
                                  "relative shrink-0",
                                  "w-[140px] h-9",
                                  "sm:w-[106px] sm:h-8",
                                  "md:w-[186px] md:h-13",
                                  "lg:w-[194px] lg:h-15",
                                  "xl:w-[220px] xl:h-[70px]",
                                )}
                              >
                                <Image
                                  src={item.image}
                                  alt={item.alt_image || "Accreditation"}
                                  fill
                                  className="object-contain"
                                  priority
                                />
                              </div>
                            ),
                        )}
                      </div>
  </RevealOnScroll>
                    </div>
                  </div>
                )}
            

              {/* CTA Buttons — mobile: order-4, Apply Now first via flex-col-reverse */}
              {/* order-4 must be on the RevealOnScroll itself — both columns use `contents` on mobile,
                  making their children direct flex items of the outer container. order on inner divs
                  has no effect once RevealOnScroll is in between. */}
              <RevealOnScroll
                y={0}
                scaleStart={0.5}
                threshold={0}
                className="order-4 sm:order-0 w-full"
                enableAnimation={isAnimationEnabled}
              >
                <div className="flex flex-col-reverse sm:flex-row items-stretch gap-4 w-full px-6 sm:px-0 py-4 sm:py-0 sm:mt-auto">
                  {/* Primary CTA: Download Brochure
                    Behaviour: Lead form popup opens upon click. Program brochure
                    downloads post successful lead drop. If download is
                    unsuccessful then option to trigger download manually in the
                    download brochure card. */}
                  {download_brochure_text && (
                    <Button
                      onClick={handleDownloadBrochure}
                      data-cta-name="download_brochure"
                      className={cn(
                        "flex items-center justify-center gap-2.5",
                        "px-6 py-4 rounded-none",
                        "text-base font-semibold -tracking-016",
                        "transition-all hover:opacity-90",
                      )}
                      style={{
                        backgroundColor: button_color,
                        color: icon_color,
                        fontFamily: fonts.cta,
                      }}
                    >
                      {download_brochure_text}
                      {icon ? (
                        <div className="relative w-6 h-6">
                          <Image
                            src={icon}
                            alt={alt_icon}
                            fill
                            className="object-contain"
                          />
                        </div>
                      ) : (
                        <svg
                          className="w-6 h-6"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 5v14" />
                          <path d="m19 12-7 7-7-7" />
                        </svg>
                      )}
                    </Button>
                  )}

                  {/* Secondary CTA: Apply Now
                    Text: Customizable through CMS
                    Behaviour: Lead form popup opens upon click. Registered
                    successfully card post successful lead drop. */}
                  {apply_now_text && (
                    <Button
                      onClick={handleApplyNow}
                      data-cta-name="apply_now"
                      className={cn(
                        "flex items-center justify-center",
                        "px-6 py-[13px] rounded-none",
                        "text-base font-semibold -tracking-016",
                        "transition-all btn-base hover:opacity-90",
                      )}
                      style={{
                        border: `3px solid ${apply_now_border_color}`,
                        color: apply_now_color,
                        backgroundColor: "rgba(0,0,0,0.3)",
                        fontFamily: fonts.cta,
                      }}
                    >
                      {apply_now_text}
                    </Button>
                  )}
                </div>
              </RevealOnScroll>
            </div>

            {/* ─── Right Column - Image Carousel ─────────────────────── */}

            <div className="contents sm:flex sm:flex-1 sm:flex-col sm:items-end sm:w-full">
              {/* Carousel — mobile: order-2 */}
              {/* order-2 must be on the RevealOnScroll itself — same reason as CTA buttons above */}
              <RevealOnScroll
                x={200}
                scaleStart={0.5}
                className={cn(
                  "order-2 sm:order-0 w-full",
                  // Lead-form slot: media still renders on mobile (the PRD keeps
                  // mobile on media regardless), it is only hidden from ≥1080.
                  leadFormTakesSlot && "sm:hidden",
                )}
                enableAnimation={isAnimationEnabled}
              >
                <div className="w-full">
                  <div className="relative w-full max-w-[362px] sm:max-w-[530px] md:max-w-[620px] lg:max-w-[659px] mx-auto sm:mx-0 sm:ml-auto">
                    {/* Images
                    Max Images: 3
                    Behaviour: Auto scroll frequency as per CMS. Navigation dots
                    visible only when more than one image uploaded. No arrow
                    option but can be swiped through.
                    Option to enable video on any image. */}

                    {/* No CSS border here on purpose: the uploaded artwork
                        carries its own border, so a second one drawn in CSS
                        doubled it up. */}
                    <div className="relative w-[335px] h-[285px] mx-auto sm:w-full sm:h-[450px] md:h-[527px] lg:h-[560px] rounded-[24px] sm:rounded-[40px] overflow-hidden">
                      {/* When a background video is uploaded it replaces the image
                          carousel entirely — no slides, no dots. It autoplays
                          muted (required for autoplay), loops, and has no controls. */}
                      {background_video ? (
                        <video
                          className="absolute inset-0 h-full w-full object-cover"
                          autoPlay
                          loop
                          muted
                          playsInline
                          preload="auto"
                          aria-label="Banner video"
                        >
                          <source
                            src={background_video}
                            type={getVideoMime(background_video)}
                          />
                        </video>
                      ) : /* If there is only ONE image, render it directly and DO NOT mount the
                          carousel. Embla (Carousel1) initializes via JS after hydration and
                          positions its slides with transforms — wrapping the single hero image
                          in it delayed the image's paint and downgraded LCP. With one image
                          there is nothing to slide, so we skip Embla entirely. */
                      activeBannerImages.length > 1 ? (
                        <Carousel1
                          autoplayDelay={slide_frequency}
                          options={{ loop: true, duration: 45 }}
                          onSlideChange={(index) => setCurrentSlideIndex(index)}
                          navigationButtonClassName="hidden"
                        >
                          {activeBannerImages.map(renderBannerSlide)}
                        </Carousel1>
                      ) : (
                        activeBannerImages.map(renderBannerSlide)
                      )}
                    </div>

                    {/* Carousel Navigation Dots
                    Visible only when more than one image uploaded and no video */}
                    {!background_video && activeBannerImages.length > 1 && (
                      <div className="absolute bottom-[18px] md:bottom-[20px] left-1/2 -translate-x-1/2 z-10 flex gap-[6px] items-center">
                        {activeBannerImages.map((_, index) => (
                          <div
                            key={index}
                            className={cn(
                              "rounded-[15px] shrink-0 transition-all duration-300",
                              currentSlideIndex === index
                                ? "w-[36px] h-[8px] md:w-[37.5px] md:h-[7.5px]"
                                : "w-[7.5px] h-[7.5px] bg-white",
                            )}
                            style={
                              currentSlideIndex === index
                                ? {
                                    backgroundColor:
                                      carousel_color || "#c8faff",
                                  }
                                : undefined
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </RevealOnScroll>

              {/* ─── Lead form — desktop only, replaces the media above ───
                  Never rendered below 1080: the wrapper is `hidden sm:flex`, and
                  the component itself is mount-gated on the viewport so mobile
                  pays none of its JS. `sm:justify-end` mirrors the media's
                  `sm:ml-auto` so the card sits flush right in the column.
                  The form supplies its own width (375px) and min-heights; the
                  placeholder below matches them so hydration cannot shift the
                  layout. */}
              {leadFormTakesSlot && (
                /* `sm:[&>*]:mx-0` neutralises any `mx-auto` the form variant puts
                   on its own root. An auto side-margin on a flex child absorbs the
                   free space and OVERRIDES `justify-end`, so those variants centred
                   themselves in this column instead of sitting flush right against
                   the copy. Lead forms 9, 5002 and 2001 all do this; variant 2 has
                   no auto margin, so this is a no-op for it. Scoped to this slot so
                   the other hosts — which centre the card deliberately — are
                   untouched. */
                <div className="hidden w-full sm:flex sm:justify-end sm:[&>*]:mx-0">
                  {isDesktopViewport && LeaddropFormComponent ? (
                    <LeaddropFormComponent
                      widthClassName={LEAD_FORM_SLOT_WIDTH}
                      timerClassNames={LEADDROP_FORM_2_DESIGN_TIMER}
                    />
                  ) : (
                    <div
                      aria-hidden
                      className={cn("min-h-[620px]", LEAD_FORM_SLOT_WIDTH)}
                    />
                  )}
                </div>
              )}

              {/* Learner/Rating — mobile: order-3.
                  Belongs to the media option, so from ≥1080 it disappears when
                  the lead form takes the slot. Mobile always keeps it, since
                  mobile always shows the media. */}

              <div
                className={cn(
                  "order-3 sm:order-0 flex flex-row items-center justify-center gap-[10px] sm:gap-5 lg:gap-10 mt-2 sm:mt-4 lg:mt-6 w-full max-w-[362px] sm:max-w-[530px] md:max-w-[620px] lg:max-w-[659px] mx-auto sm:mx-0 sm:ml-auto overflow-hidden",
                  leadFormTakesSlot && "sm:hidden",
                )}
              >
                {/* Alumni
                    Max Images: 3 · Text size: Max 16 characters */}
                {show_learners === 1 &&
                  (learners_description || activeLearnerImages.length > 0) && (
                    <div className="flex items-center gap-2.5 min-w-0 overflow-hidden">
                      {activeLearnerImages.length > 0 && (
                        <RevealOnScroll
                          y={50}
                          x={-50}
                          threshold={0}
                          scaleStart={0.5}
                          duration={600}
                          enableAnimation={isAnimationEnabled}
                        >
                          <div className="flex shrink-0 h-5 md:h-[30px] lg:h-[40px]">
                            {activeLearnerImages.map((item, index) => (
                              <div
                                key={item.id || index}
                                className={cn(
                                  "relative w-5 h-5 md:w-[30px] md:h-[30px] lg:w-[40px] lg:h-[40px] rounded-full overflow-hidden shrink-0",
                                  index !== 0 &&
                                    "-ml-[6px] md:-ml-[10px] lg:-ml-[14px]",
                                )}
                                style={{ zIndex: index }}
                              >
                                <Image
                                  src={item.image}
                                  alt={
                                    item.description
                                      ? item.description.slice(
                                          0,
                                          MAX_LEARNER_TEXT_CHARS,
                                        )
                                      : "Learner"
                                  }
                                  fill
                                  className="object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        </RevealOnScroll>
                      )}
                      {learners_description && (
                        <RevealOnScroll
                          threshold={0}
                          y={30}
                          enableAnimation={isAnimationEnabled}
                        >
                          <div
                            ref={alumniTextRef}
                            className="text-xs sm:text-[15px] md:text-[16px] lg:text-[20px] font-semibold leading-[1.5] text-white truncate min-w-0 max-w-[150px] sm:max-w-[200px] md:max-w-[280px] lg:max-w-[350px] [&_p]:inline [&_strong]:inline [&_span]:inline [&_em]:inline cursor-pointer"
                            style={{ fontFamily: fonts.learners }}
                            dangerouslySetInnerHTML={{
                              __html: learners_description,
                            }}
                            onMouseEnter={handleAlumniMouseEnter}
                            onMouseLeave={handleAlumniMouseLeave}
                          />
                        </RevealOnScroll>
                      )}
                    </div>
                  )}

                {/* Vertical Separator */}
                {show_rating === 1 &&
                  show_learners === 1 &&
                  (ratingForStars > 0 || clampedRatingText) &&
                  learners_description && (
                    <div className="w-px h-5 sm:h-[30px] shrink-0 bg-[#FFFFFF33]" />
                  )}

                {/* Ratings */}
                {show_rating === 1 &&
                  (ratingForStars > 0 || clampedRatingText) && (
                    <div className="flex items-center gap-[2px]">
                      {/* Star Icons (half-star precision) */}
                      <RevealOnScroll
                        duration={1200}
                        threshold={0}
                        enableAnimation={isAnimationEnabled}
                      >
                        <StarRating
                          rating={ratingForStars}
                          color={rating_color || "#FFFFFF"}
                          className="gap-[2px] sm:gap-[2.667px]"
                        />
                      </RevealOnScroll>
                      <RevealOnScroll
                        className="w-full"
                        threshold={0}
                        y={20}
                        duration={600}
                        enableAnimation={isAnimationEnabled}
                      >
                        <span
                          className="text-xs sm:text-[15px] md:text-[16px] lg:text-[20px] font-semibold leading-[1.5]"
                          style={{
                            color: rating_number_color,
                            fontFamily: fonts.rating,
                          }}
                        >
                          {clampedRatingNumber}
                        </span>
                        {clampedRatingText && (
                          <span
                            className="text-xs sm:text-[15px] md:text-[16px] lg:text-[20px] leading-[1.5] truncate max-w-[100px] sm:max-w-[120px] md:max-w-[150px] inline-block align-middle"
                            style={{
                              color: "rgba(255,255,255,0.5)",
                              fontFamily: fonts.rating,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: clampedRatingText,
                            }}
                          />
                        )}
                      </RevealOnScroll>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alumni tooltip (fixed position to escape overflow-hidden ancestors) */}
      {show_learners === 1 && alumniTooltip.visible && learners_description && (
        <div
          className="fixed z-9999 pointer-events-none"
          style={{
            left: alumniTooltip.x,
            top: alumniTooltip.y,
            transform: "translate(-50%, -100%)",
          }}
        >
          <div className="mb-2">
            <div className="bg-gray-900 text-white text-xs sm:text-sm rounded-lg px-3 py-2 shadow-lg wrap-break-word w-max max-w-[250px] sm:max-w-[320px] text-center">
              <span
                dangerouslySetInnerHTML={{
                  __html: learners_description,
                }}
              />
            </div>
            {/* Tooltip arrow */}
            <div className="flex justify-center">
              <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900" />
            </div>
          </div>
        </div>
      )}

      {/* Video Overlay (shown when user clicks play on a video-enabled slide) */}
      {activeVideoUrl && (
        <YouTubeVideoOverlay
          isOpen={videoOpen}
          onClose={() => {
            setVideoOpen(false);
            setActiveVideoUrl("");
          }}
          videoUrl={activeVideoUrl}
        />
      )}
    </>
  );
};

export default Banner2;
